import React, { memo, useRef, useEffect } from 'react';
import { Loader2, CheckCircle, XCircle, AlertCircle, ChevronRight, ChevronDown, Clipboard, Check } from 'lucide-react';
import { MarkdownRenderer } from '../../Renderers';
import { Spinner } from '../../../../design-system';

/**
 * Component to display tool call status with appropriate icon
 */
const ToolCallIndicator = memo(({ 
  toolName, 
  status = 'pending',
  error = null,
  isExpanded = false,
  onToggleExpand,
  hasResult = false,
  onCopy,
  copiedMessageId,
  messageId,
  resultContent,
  streamingContent,
  isStreaming,
  searchQuery,
  index
}) => {
  const streamingContainerRef = useRef(null);
  
  // Auto-scroll to bottom when streaming content updates
  useEffect(() => {
    if (streamingContainerRef.current && isStreaming && streamingContent) {
      streamingContainerRef.current.scrollTop = streamingContainerRef.current.scrollHeight;
    }
  }, [streamingContent, isStreaming]);
  
  const getStatusIcon = () => {
    switch (status) {
      case 'pending':
        return <Spinner size={14} />;
      case 'streaming':
        return <Spinner size={14} />;
      case 'success':
        return <CheckCircle size={14} className="text-semantic-success" />;
      case 'error':
        return <XCircle size={14} className="text-semantic-error" />;
      case 'failed':
        return <AlertCircle size={14} className="text-semantic-warning" />;
      default:
        return <Loader2 size={14} className="text-neutral-600 dark:text-neutral-400" />;
    }
  };
  
  // Determine what content to show
  const displayContent = streamingContent || resultContent;
  const shouldShowContent = isExpanded && (displayContent || isStreaming);
  
  // Debug logging for streaming issues
  if (streamingContent || isStreaming) {
    console.log('ToolCallIndicator State:', {
      toolName,
      status,
      isExpanded,
      isStreaming,
      hasStreamingContent: !!streamingContent,
      streamingContentLength: streamingContent?.length,
      hasResultContent: !!resultContent,
      shouldShowContent,
      displayContentLength: displayContent?.length
    });
  }
  
  return (
    <>
      <div className="flex items-center mb-2 px-4 pt-3 text-xs font-medium">
        <span className="mr-2">{getStatusIcon()}</span>
        <span className="text-neutral-600 dark:text-neutral-400" style={{ fontWeight: 'bold', paddingRight: '5px' }}>
          {toolName || 'Tool'}
        </span>
        
        {(status !== 'pending' || isStreaming || streamingContent) && (
          <>
            <button 
              onClick={onToggleExpand}
              className="ml-auto text-xs text-neutral-600 dark:text-neutral-400"
              title={isExpanded ? "Show less" : "Show more"}
              aria-label={isExpanded ? "Show less" : "Show more"}
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {onCopy && (
              <button 
                onClick={onCopy}
                className="p-1.5 hover:bg-slate-200 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                title="Copy to clipboard"
                aria-label="Copy to clipboard"
              >
                {copiedMessageId === messageId ? (
                  <Check size={14} className="text-semantic-success" />
                ) : (
                  <Clipboard size={14} className="text-neutral-600 dark:text-neutral-400" />
                )}
              </button>
            )}
          </>
        )}
      </div>
      
      {shouldShowContent && (
        <div className="text-ui-text-primary px-4 pb-3">
          <div>
            {/* Special display for streaming content */}
            {isStreaming && streamingContent ? (
              <div className="streaming-content-container">
                <div 
                  ref={streamingContainerRef}
                  className="tool-streaming-content font-mono text-xs bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg p-3 max-h-48 overflow-y-auto whitespace-pre-wrap"
                  style={{ 
                    scrollBehavior: 'smooth',
                    wordBreak: 'break-word'
                  }}
                >
                  {streamingContent}
                </div>
              </div>
            ) : (
              // Regular display for completed content
              toolName === 'take_browser_screenshot' && 
              typeof displayContent === 'string' && 
              displayContent.startsWith('data:image') ? (
                <div className="screenshot-container">
                  <div className="mt-2">
                    <img 
                      src={displayContent} 
                      alt="Browser screenshot" 
                      className="w-full rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm" 
                      loading="lazy"
                    />
                  </div>
                </div>
              ) : (
                <MarkdownRenderer 
                  content={displayContent} 
                  searchQuery={searchQuery} 
                  messageIndex={index} 
                />
              )
            )}
          </div>
        </div>
      )}
    </>
  );
});

ToolCallIndicator.displayName = 'ToolCallIndicator';

export default ToolCallIndicator;